/**
 * Provider-state helpers shared by `ProviderAccountList` (the row renderer)
 * and external callers that need the same "is this provider usable?"
 * decision (e.g. `/onboard` deciding whether to show the provider section
 * at all).
 *
 * Lives in its own module so `ProviderAccountList.tsx` can stay a pure
 * component file (`react-refresh/only-export-components`).
 */

export interface ProviderEntry {
  name: string;
  label: string;
  /**
   * Whether this provider requires a stored access token to be considered
   * "connected". Sign-in-only providers set this to false. Defaults to true.
   */
  requiresStoredToken?: boolean;
}

export interface LinkedAccount {
  provider: string;
  providerUserId: string;
  providerData: Record<string, unknown> | null;
  hasAccessToken: boolean;
}

export type ProviderState = 'connected' | 'reauth' | 'disconnected';

/**
 * Resolves the visible state for a provider row. A provider is `connected`
 * only when a row exists AND either the account has a usable access token
 * or the provider is sign-in-only (`requiresStoredToken === false`). A row
 * with `hasAccessToken=false` on a token-required provider yields `reauth`.
 *
 * Used both by the row renderer and by `/onboard` to decide whether to
 * show the provider list at all — a re-auth row should still surface so
 * users in the GitHub scope-upgrade case can reconnect.
 */
export function getProviderState(
  provider: ProviderEntry,
  linked: LinkedAccount | undefined,
): ProviderState {
  if (!linked) return 'disconnected';
  if (linked.hasAccessToken || provider.requiresStoredToken === false) return 'connected';
  return 'reauth';
}
